import { z } from "zod";
import type { Operation } from "../types";

export const AGENT_IMPROVEMENT_DASHBOARD_SCHEMA_VERSION = 1 as const;

const namespacedId = z.string().min(1).max(240);
const iso = z.string().datetime({ offset: true });
const envName = z.enum(["demo", "real"]);
const modelUsageCostSource = z.enum(["vendor", "hosted", "estimated", "local_compute", "unknown"]);
const costClaimState = z.enum(["complete", "partial", "unknown"]);
const MIN_DEMO_CONTROLLED_RECORDS = 24;
const MAX_EMBEDDED_BINARY_BASE64_CHARS = 30_000_000;
const embeddedBase64 = z.string().min(1).max(MAX_EMBEDDED_BINARY_BASE64_CHARS);

export const agentImprovementAttributionClass = z.enum([
  "insufficient-data",
  "observed",
  "associated-after-exposure",
  "controlled-improvement",
  "regressed",
]);
export type AgentImprovementAttributionClass = z.infer<typeof agentImprovementAttributionClass>;

export const agentConnectionState = z.enum([
  "connected",
  "configured",
  "observed",
  "needs_setup",
  "unknown",
]);
export type AgentConnectionState = z.infer<typeof agentConnectionState>;

export const agentIntegrationMode = z.enum([
  "fully_mediated",
  "cooperative",
  "passive",
  "unsupported",
  "unknown",
]);
export type AgentIntegrationMode = z.infer<typeof agentIntegrationMode>;

const agentIntegrationClaimLimitsDto = z.object({
  canClaimActiveImprovement: z.boolean(),
  canClaimEnforcement: z.boolean(),
  canClaimVerification: z.boolean(),
  maxInjectedLearningTokens: z.number().int().nonnegative(),
  unsupportedReason: z.string().min(1).nullable(),
}).strict();
export type AgentIntegrationClaimLimitsDto = z.infer<typeof agentIntegrationClaimLimitsDto>;

export const agentAdapterPerformanceStatus = z.enum(["nominal", "degraded", "unknown"]);
export type AgentAdapterPerformanceStatus = z.infer<typeof agentAdapterPerformanceStatus>;

export const agentAdapterSuppressedClaim = z.enum(["speed", "cost", "accuracy"]);
export type AgentAdapterSuppressedClaim = z.infer<typeof agentAdapterSuppressedClaim>;

export const learningExposureStatus = z.enum([
  "selected",
  "emitted",
  "acknowledged",
  "applied",
  "failed",
  "stale",
]);
export type LearningExposureStatus = z.infer<typeof learningExposureStatus>;

export const learningVersionState = z.enum([
  "candidate",
  "admitted",
  "validated",
  "suppressed",
  "retired",
  "promoted",
]);
export type LearningVersionState = z.infer<typeof learningVersionState>;

export const learningExposureSourceSurface = z.enum([
  "cli_chat",
  "fleet_worker",
  "dock_acp",
  "external_api",
]);
export type LearningExposureSourceSurface = z.infer<typeof learningExposureSourceSurface>;

export const legalLearningExposureTransitions = {
  selected: ["emitted", "failed", "stale"],
  emitted: ["acknowledged", "failed"],
  acknowledged: ["applied", "stale"],
  failed: ["failed"],
  applied: ["applied"],
  stale: ["stale"],
} as const satisfies Record<LearningExposureStatus, readonly LearningExposureStatus[]>;

export const terminalLearningExposureStatuses = ["failed", "applied", "stale"] as const;

export const agentImprovementQuery = z.object({
  env: envName.optional(),
  actorId: namespacedId.optional(),
}).strict();
export type AgentImprovementQuery = z.infer<typeof agentImprovementQuery>;

export const agentImprovementProvenanceDto = z.object({
  environment: envName,
  workspaceId: namespacedId,
  actorId: namespacedId.nullable(),
  asOf: iso,
  demoScoped: z.boolean(),
});
export type AgentImprovementProvenanceDto = z.infer<typeof agentImprovementProvenanceDto>;

export const agentConnectionEvidenceDto = z.object({
  workspaceId: namespacedId,
  environment: envName,
  agentId: namespacedId,
  provider: z.string().min(1).max(80).nullable(),
  model: z.string().min(1).max(160).nullable(),
  hasMalformedOrCrossWorkspaceOrPermissionBlockedRecords: z.boolean(),
  hasEvidenceSourceUnavailable: z.boolean(),
  hasActiveLiveReportInsideTtl: z.boolean(),
  hasValidIntegrationConfigInsideTtl: z.boolean(),
  hasWorkspaceScopedHistoricalObservation: z.boolean(),
  liveSessionReportId: namespacedId.nullable(),
  sessionLifecycle: z.enum(["active", "running", "terminal", "unknown"]),
  currentSessionTtlSeconds: z.number().int().positive().nullable(),
  latestHeartbeatAt: iso.nullable(),
  latestRunId: namespacedId.nullable(),
  latestClaimId: namespacedId.nullable(),
  integrationMode: agentIntegrationMode,
  claimLimits: agentIntegrationClaimLimitsDto,
  adapterIntegrationId: namespacedId.nullable().optional(),
  adapterNegotiatedAt: iso.nullable().optional(),
  adapterStaleAt: iso.nullable().optional(),
  adapterClaimLimitsSource: z.enum(["negotiated", "missing", "stale"]).optional(),
  adapterPerformanceStatus: agentAdapterPerformanceStatus.optional(),
  adapterDegradedReason: z.string().min(1).nullable().optional(),
  adapterSuppressedClaims: z.array(agentAdapterSuppressedClaim).optional(),
  asOf: iso,
});
export type AgentConnectionEvidenceDto = z.infer<typeof agentConnectionEvidenceDto>;

export const agentConnectionDto = agentConnectionEvidenceDto.extend({
  state: agentConnectionState,
  reason: z.string().min(1),
}).superRefine((connection, ctx) => {
  const resolved = resolveAgentConnectionState(connection);
  if (connection.state !== resolved) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["state"],
      message: `connection state must match evidence predicates (${resolved})`,
    });
  }
  if (connection.state === "connected") {
    if (!connection.liveSessionReportId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["liveSessionReportId"],
        message: "connected rows require a live session/report id",
      });
    }
    if (!connection.latestHeartbeatAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["latestHeartbeatAt"],
        message: "connected rows require latestHeartbeatAt",
      });
    }
    if (connection.sessionLifecycle !== "active" && connection.sessionLifecycle !== "running") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sessionLifecycle"],
        message: "connected rows require an active or running session lifecycle",
      });
    }
    if (!connection.currentSessionTtlSeconds) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["currentSessionTtlSeconds"],
        message: "connected rows require currentSessionTtlSeconds",
      });
    }
    if (
      connection.latestHeartbeatAt &&
      connection.currentSessionTtlSeconds &&
      elapsedFromIso(connection.latestHeartbeatAt, Date.parse(connection.asOf)) >
        connection.currentSessionTtlSeconds * 1000
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["latestHeartbeatAt"],
        message: "connected heartbeat must be inside currentSessionTtlSeconds",
      });
    }
  }
  if (connection.adapterPerformanceStatus === "degraded") {
    const suppressed = new Set(connection.adapterSuppressedClaims ?? []);
    if (suppressed.size === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["adapterSuppressedClaims"],
        message: "degraded adapter rows must name suppressed claim categories",
      });
    }
    if (suppressed.has("accuracy") && connection.claimLimits.canClaimActiveImprovement) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["claimLimits", "canClaimActiveImprovement"],
        message: "degraded accuracy suppression forbids active-improvement claims",
      });
    }
    if (suppressed.has("speed") && connection.claimLimits.canClaimEnforcement) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["claimLimits", "canClaimEnforcement"],
        message: "degraded speed suppression forbids enforcement claims",
      });
    }
    if (suppressed.has("cost") && connection.claimLimits.unsupportedReason === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["claimLimits", "unsupportedReason"],
        message: "degraded cost suppression requires a claim-limit reason",
      });
    }
  }
});
export type AgentConnectionDto = z.infer<typeof agentConnectionDto>;

const receiverAcknowledgementEvidenceDto = z.object({
  receiverAckId: namespacedId.optional(),
  receiverEchoedContextHash: z.string().min(1).optional(),
  receiverConfirmedResponseId: namespacedId.optional(),
  receivingSessionId: namespacedId,
  contextVersionHash: z.string().min(1),
});
export type ReceiverAcknowledgementEvidenceDto = z.infer<typeof receiverAcknowledgementEvidenceDto>;

const appliedEvidenceDto = z.object({
  downstreamRunId: namespacedId.optional(),
  downstreamClaimId: namespacedId.optional(),
  receivingSessionId: namespacedId,
  contextVersionHash: z.string().min(1),
  exposureId: namespacedId.optional(),
  beforeEvidenceRef: namespacedId.optional(),
  afterEvidenceRef: namespacedId.optional(),
  oracleReceiptRef: namespacedId.optional(),
  localModelInvocationId: z.string().min(1).optional(),
  correctionPacketRef: namespacedId.nullable().optional(),
});
export type AppliedEvidenceDto = z.infer<typeof appliedEvidenceDto>;

const failureEvidenceDto = z.object({
  kind: z.enum([
    "transport_error",
    "receiver_rejection",
    "timeout",
    "context_hash_mismatch",
    "selector_denied",
    "packet_guardrail_denied",
  ]),
  message: z.string().min(1),
});
export type FailureEvidenceDto = z.infer<typeof failureEvidenceDto>;

const staleEvidenceDto = z.object({
  ttlKind: z.enum(["selected_to_emitted", "acknowledged_to_applied"]),
  anchorTimestamp: iso,
  evaluatedAt: iso,
  ttlMs: z.number().int().positive(),
});
export type StaleEvidenceDto = z.infer<typeof staleEvidenceDto>;

export const learningExposureAttemptDto = z
  .object({
    id: namespacedId,
    workspaceId: namespacedId,
    environment: envName,
    logicalExposureKey: z.string().min(1),
    attemptNumber: z.number().int().positive(),
    learningId: namespacedId,
    receivingSessionId: namespacedId,
    receivingAgentId: namespacedId,
    provider: z.string().min(1).max(80).nullable(),
    model: z.string().min(1).max(160).nullable(),
    contextVersionHash: z.string().min(1),
    status: learningExposureStatus,
    sourceSurface: learningExposureSourceSurface,
    selectedAt: iso,
    emittedAt: iso.nullable(),
    acknowledgedAt: iso.nullable(),
    appliedAt: iso.nullable(),
    failedAt: iso.nullable(),
    staleAt: iso.nullable(),
    acknowledgementEvidence: receiverAcknowledgementEvidenceDto.nullable(),
    appliedEvidence: appliedEvidenceDto.nullable(),
    failureEvidence: failureEvidenceDto.nullable(),
    staleEvidence: staleEvidenceDto.nullable(),
    selectorDecisionRef: namespacedId.nullable().optional(),
    selectorEvidence: z.record(z.string(), z.unknown()).nullable().optional(),
    guardrailDecisionId: namespacedId.nullable().optional(),
    packetHash: z.string().regex(/^[a-f0-9]{64}$/).nullable().optional(),
    promotionEvidence: z.record(z.string(), z.unknown()).nullable().optional(),
    promotedAt: iso.nullable().optional(),
    retryCount: z.number().int().nonnegative(),
    lastRetryAt: iso.nullable(),
  })
  .superRefine((attempt, ctx) => {
    const expectedKey = logicalExposureKeyFor(attempt);
    if (attempt.logicalExposureKey !== expectedKey) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["logicalExposureKey"],
        message: `logicalExposureKey must be derived from learning/session/context/source (${expectedKey})`,
      });
    }
    if (attempt.status === "emitted" && !attempt.emittedAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["emittedAt"],
        message: "emitted exposures require emittedAt",
      });
    }
    if (attempt.emittedAt && Date.parse(attempt.selectedAt) > Date.parse(attempt.emittedAt)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["emittedAt"],
        message: "emittedAt must be after selectedAt",
      });
    }
    if ((attempt.status === "acknowledged" || attempt.status === "applied") && !hasBoundAcknowledgement(attempt)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["acknowledgementEvidence"],
        message: "delivered exposures require receiver-originated acknowledgement bound to session and context",
      });
    }
    if ((attempt.status === "acknowledged" || attempt.status === "applied") && !attempt.emittedAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["emittedAt"],
        message: "acknowledged/applied exposures require emittedAt",
      });
    }
    if ((attempt.status === "acknowledged" || attempt.status === "applied") && !attempt.acknowledgedAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["acknowledgedAt"],
        message: "acknowledged/applied exposures require acknowledgedAt",
      });
    }
    if (
      attempt.emittedAt &&
      attempt.acknowledgedAt &&
      Date.parse(attempt.emittedAt) > Date.parse(attempt.acknowledgedAt)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["acknowledgedAt"],
        message: "acknowledgedAt must be after emittedAt",
      });
    }
    if (attempt.status === "applied" && !hasBoundApplication(attempt)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["appliedEvidence"],
        message: "applied exposures require downstream run/claim evidence bound to session and context",
      });
    }
    if (attempt.status === "applied" && !attempt.appliedAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["appliedAt"],
        message: "applied exposures require appliedAt",
      });
    }
    if (
      attempt.acknowledgedAt &&
      attempt.appliedAt &&
      Date.parse(attempt.acknowledgedAt) > Date.parse(attempt.appliedAt)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["appliedAt"],
        message: "appliedAt must be after acknowledgedAt",
      });
    }
    if (attempt.status === "failed" && (!attempt.failureEvidence || !attempt.failedAt)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["failureEvidence"],
        message: "failed exposures require failure timestamp and evidence",
      });
    }
    if (attempt.status === "stale" && (!attempt.staleEvidence || !attempt.staleAt)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["staleEvidence"],
        message: "stale exposures require stale timestamp and TTL expiry evidence",
      });
    }
    if (attempt.status === "stale" && attempt.staleEvidence?.ttlKind === "selected_to_emitted") {
      if (
        attempt.staleEvidence.anchorTimestamp !== attempt.selectedAt ||
        attempt.emittedAt !== null ||
        !attempt.staleAt ||
        Date.parse(attempt.staleAt) < Date.parse(attempt.selectedAt) + attempt.staleEvidence.ttlMs ||
        Date.parse(attempt.staleEvidence.evaluatedAt) < Date.parse(attempt.selectedAt) + attempt.staleEvidence.ttlMs
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["staleEvidence"],
          message: "selected_to_emitted stale evidence must anchor to selectedAt and prove TTL expiry before emission",
        });
      }
    }
    if (attempt.status === "stale" && attempt.staleEvidence?.ttlKind === "acknowledged_to_applied") {
      if (
        !attempt.emittedAt ||
        !attempt.acknowledgedAt ||
        !hasBoundAcknowledgement(attempt) ||
        attempt.staleEvidence.anchorTimestamp !== attempt.acknowledgedAt ||
        !attempt.staleAt ||
        Date.parse(attempt.staleAt) < Date.parse(attempt.acknowledgedAt) + attempt.staleEvidence.ttlMs ||
        Date.parse(attempt.staleEvidence.evaluatedAt) <
          Date.parse(attempt.acknowledgedAt) + attempt.staleEvidence.ttlMs
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["staleEvidence"],
          message: "acknowledged_to_applied stale evidence must anchor to acknowledgedAt and prove TTL expiry after receiver acknowledgement",
        });
      }
    }
  });
export type LearningExposureAttemptDto = z.infer<typeof learningExposureAttemptDto>;

export const improvementEvidenceManifestDto = z.object({
  id: namespacedId,
  workspaceId: namespacedId,
  environment: envName,
  attributionClass: agentImprovementAttributionClass,
  numeratorRecordIds: z.array(namespacedId),
  denominatorRecordIds: z.array(namespacedId),
  exposureIds: z.array(namespacedId),
  comparisonUnit: z.string().min(1).nullable(),
  policyVersion: z.string().min(1).nullable(),
  methodVersion: z.string().min(1),
  sampleSize: z.number().int().nonnegative(),
  uncertaintyInterval: z
    .object({
      low: z.number(),
      high: z.number(),
      unit: z.string().min(1),
    })
    .nullable(),
  window: z.object({
    startsAt: iso,
    endsAt: iso,
  }),
  exclusions: z.array(z.string().min(1)),
  completeness: z.object({
    knownSourceCount: z.number().int().nonnegative(),
    expectedSourceCount: z.number().int().nonnegative(),
    unknownCostCoverage: z.boolean(),
  }),
  counterfactualBasis: z.string().min(1).nullable(),
  sourceLinks: z.array(z.string().min(1)),
  reviewerApprovedAt: iso.nullable(),
}).superRefine((manifest, ctx) => {
  const startsAt = Date.parse(manifest.window.startsAt);
  const endsAt = Date.parse(manifest.window.endsAt);
  if (Number.isNaN(startsAt) || Number.isNaN(endsAt) || startsAt >= endsAt) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["window"],
      message: "manifest window must be a valid increasing time range",
    });
  }
  if (manifest.uncertaintyInterval && manifest.uncertaintyInterval.low > manifest.uncertaintyInterval.high) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["uncertaintyInterval"],
      message: "uncertainty interval low must be <= high",
    });
  }
});
export type ImprovementEvidenceManifestDto = z.infer<typeof improvementEvidenceManifestDto>;

export const improvementMetricDto = z.object({
  id: namespacedId,
  workspaceId: namespacedId,
  environment: envName,
  label: z.string().min(1),
  attributionClass: agentImprovementAttributionClass,
  value: z.number().nullable(),
  valueLabel: z.string().min(1),
  unit: z.string().min(1),
  baselineValue: z.number().nullable(),
  baselineValueLabel: z.string().min(1),
  currentValue: z.number().nullable(),
  currentValueLabel: z.string().min(1),
  evidenceManifestId: namespacedId.nullable(),
});
export type ImprovementMetricDto = z.infer<typeof improvementMetricDto>;

export const agentLearningSummaryDto = z.object({
  id: namespacedId,
  workspaceId: namespacedId,
  environment: envName,
  title: z.string().min(1),
  summary: z.string().min(1),
  behaviorChange: z.string().min(1),
  sourceSessionId: namespacedId.nullable(),
  approvedAt: iso.nullable(),
  exposureId: namespacedId.nullable(),
  learningVersionHash: z.string().min(1).nullable().optional(),
  learningVersionState: learningVersionState.nullable().optional(),
  counterevidenceRefs: z.array(namespacedId).optional(),
  wrongRecommendationRecovery: z.object({
    badOutcomeClass: z.enum(["harmful", "invalid", "unsafe", "superseded", "no_effect", "inefficient", "over_intelligent", "policy_risk"]),
    originalRecommendationRef: namespacedId,
    counterevidenceRef: namespacedId,
    selectorDecision: z.enum(["suppress", "retire"]),
    selectorDecisionRef: namespacedId,
    correctedNextAgentPacketRef: namespacedId,
  }).nullable().optional(),
  experimentContaminationRef: namespacedId.nullable().optional(),
  status: z.enum(["proposed", "approved", "delivered", "applied", "unverified"]),
});
export type AgentLearningSummaryDto = z.infer<typeof agentLearningSummaryDto>;

export const modelRoutingComparisonDto = z.object({
  id: namespacedId,
  workspaceId: namespacedId,
  environment: envName,
  routeKey: z.string().min(1),
  provider: z.string().min(1).max(80),
  model: z.string().min(1).max(160),
  sampleSize: z.number().int().nonnegative(),
  costSampleSize: z.number().int().nonnegative(),
  successRate: z.number().min(0).max(1).nullable(),
  successRateLabel: z.string().min(1),
  successDeltaLabel: z.string().min(1).optional(),
  successDeltaState: z.enum(["hidden", "controlled"]).optional(),
  averageCostUsd: z.number().nonnegative().nullable(),
  averageCostLabel: z.string().min(1),
  costClaimState,
  averageDurationMs: z.number().int().nonnegative().nullable(),
  averageDurationLabel: z.string().min(1),
  attributionClass: agentImprovementAttributionClass,
  evidenceManifestId: namespacedId.nullable(),
  recommended: z.boolean(),
}).superRefine((row, ctx) => {
  if (row.averageCostUsd !== null && row.costClaimState !== "complete") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["averageCostUsd"],
      message: "numeric averageCostUsd requires complete cost claim state",
    });
  }
  if (row.costSampleSize > row.sampleSize) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["costSampleSize"],
      message: "costSampleSize cannot exceed sampleSize",
    });
  }
  if (row.costClaimState === "complete" && row.costSampleSize !== row.sampleSize) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["costSampleSize"],
      message: "complete route cost claims require every exposure sample to have cost evidence",
    });
  }
  if (row.recommended && row.costClaimState !== "complete") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["costClaimState"],
      message: "recommended routing rows require complete cost claim state",
    });
  }
});
export type ModelRoutingComparisonDto = z.infer<typeof modelRoutingComparisonDto>;

export const localVerificationModelDto = z.object({
  id: namespacedId,
  workspaceId: namespacedId,
  environment: envName,
  provider: z.enum(["ollama", "lmstudio", "vllm", "llamacpp"]),
  model: z.string().min(1).max(160),
  lockId: namespacedId,
  endpoint: z.string().url(),
  role: z.string().min(1),
  status: z.enum(["active", "configured", "required", "unavailable"]),
  evidenceSource: z.string().min(1),
  share: z.number().min(0).max(1).nullable(),
  shareLabel: z.string().min(1),
  sampleSize: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative().nullable(),
  costLabel: z.string().min(1),
  costSource: modelUsageCostSource.nullable(),
  costClaimState,
  liveCheckedAt: iso.nullable(),
  receiptEvidence: z.object({
    receiptCount: z.number().int().nonnegative(),
    failedReceiptCount: z.number().int().nonnegative().optional(),
    rejectedReceiptCount: z.number().int().nonnegative().optional(),
    latestReceiptId: namespacedId.nullable(),
    latestInvocationId: namespacedId.nullable(),
    latestPredicateVersionId: namespacedId.nullable(),
    latestInterventionId: namespacedId.nullable(),
    latestExposureId: namespacedId.nullable(),
    latestCreatedAt: iso.nullable(),
    fallbackProvider: z.null(),
    latestRoleExecutions: z.array(z.object({
      role: z.enum([
        "work_classifier",
        "predicate_compiler",
        "trace_critic",
        "route_scorer",
        "sensitive_summarizer",
        "learning_distiller",
      ]),
      mandatory: z.boolean(),
      status: z.enum(["accepted", "rejected", "failed", "missing", "not_required"]),
      admissionResult: z.enum(["accepted", "rejected", "failed"]).nullable(),
      claimSuppressed: z.boolean(),
      reason: z.string().min(1).nullable(),
      promptHash: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
      outputHash: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
      evidenceRefs: z.array(z.string().min(1)),
      durationMs: z.number().int().nonnegative().nullable(),
      localComputeCostUsd: z.number().nonnegative().nullable(),
      influencedObjectIds: z.object({
        predicateVersionId: z.string().min(1).nullable(),
        interventionId: z.string().min(1).nullable(),
        exposureId: z.string().min(1).nullable(),
        learningId: z.string().min(1).nullable(),
      }),
    })).optional(),
  }),
  operatorAction: z.string().min(1).nullable(),
  detail: z.string().min(1),
}).superRefine((row, ctx) => {
  if (row.costUsd !== null && row.costClaimState !== "complete") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["costUsd"],
      message: "numeric local model cost requires complete cost claim state",
    });
  }
  if (row.costClaimState === "complete" && row.costSource !== "local_compute") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["costSource"],
      message: "complete local verifier costs require local_compute source",
    });
  }
  if (row.status === "active" && row.sampleSize === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["sampleSize"],
      message: "active local verification models require at least one scoped sample",
    });
  }
  if (row.status === "active" && row.receiptEvidence.receiptCount === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["receiptEvidence", "receiptCount"],
      message: "active local verification models require a scoped immutable local invocation receipt",
    });
  }
  if (row.status === "active" && !row.liveCheckedAt) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["liveCheckedAt"],
      message: "active local verification models require liveCheckedAt",
    });
  }
});
export type LocalVerificationModelDto = z.infer<typeof localVerificationModelDto>;

export const suppressedFactDto = z.object({
  source: z.string().min(1),
  reason: z.enum(["unscoped", "permission_blocked", "stale", "incomplete"]),
  category: z.enum(["learning", "routing", "cost", "manifest", "local_model", "source"]),
  claimImpact: z.enum([
    "blocks_learning_delivery",
    "blocks_route_recommendation",
    "blocks_test_cost_or_savings",
    "blocks_controlled_improvement",
    "blocks_local_verification",
    "blocks_source_inspection",
  ]),
}).superRefine((fact, ctx) => {
  const expectedImpactByCategory = {
    learning: "blocks_learning_delivery",
    routing: "blocks_route_recommendation",
    cost: "blocks_test_cost_or_savings",
    manifest: "blocks_controlled_improvement",
    local_model: "blocks_local_verification",
    source: "blocks_source_inspection",
  } as const;
  if (fact.claimImpact !== expectedImpactByCategory[fact.category]) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["claimImpact"],
      message: "suppressed fact claimImpact must match category",
    });
  }
});

const claimSummaryItemDto = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
  state: z.enum([
    "claim_ready",
    "ready",
    "delivered",
    "applied",
    "recommended",
    "active",
    "projected",
    "tracked",
    "observed_only",
    "configured",
    "required",
    "unavailable",
    "not_claimable",
    "missing",
    "gated",
    "not_counted",
    "not_inspected",
    "clear",
  ]),
  target: z.string().min(1),
  detail: z.string().min(1),
});

export const agentImprovementClaimSummaryDto = z.object({
  claim: claimSummaryItemDto,
  learned: claimSummaryItemDto,
  handoff: claimSummaryItemDto,
  application: claimSummaryItemDto,
  routeAction: claimSummaryItemDto,
  localVerifier: claimSummaryItemDto,
  value: claimSummaryItemDto,
  excludedEvidence: claimSummaryItemDto,
  nextAction: z.string().min(1),
});
export type AgentImprovementClaimSummaryDto = z.infer<typeof agentImprovementClaimSummaryDto>;

const evidenceDetailRowDto = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
  status: z.string().min(1),
  intent: z.enum(["success", "info", "warning", "neutral"]),
  detail: z.string().min(1),
});

export const agentImprovementEvidenceDetailsDto = z.object({
  modelDecision: z.array(evidenceDetailRowDto).min(1),
  valueBrief: z.array(evidenceDetailRowDto).min(1),
  claimBoundary: z.array(evidenceDetailRowDto).min(1),
});
export type AgentImprovementEvidenceDetailsDto = z.infer<typeof agentImprovementEvidenceDetailsDto>;

const guardrailDecisionDto = z.object({
  id: namespacedId,
  workspaceId: namespacedId,
  actorId: namespacedId,
  requestId: namespacedId,
  sessionUrn: namespacedId.nullable(),
  policyId: z.string().min(1),
  policyVersion: z.string().min(1),
  guardName: z.string().min(1),
  actionClass: z.string().min(1),
  sensitivity: z.string().min(1),
  payloadKind: z.string().min(1),
  decision: z.enum(["allow", "deny", "redact", "substitute", "approval_required"]),
  reasonCode: z.string().min(1),
  redactionManifestRef: namespacedId.nullable(),
  substituteRef: namespacedId.nullable(),
  approvalPolicy: z.string().min(1).nullable(),
  auditRef: namespacedId,
  createdAt: iso,
});
export type GuardrailDecisionDto = z.infer<typeof guardrailDecisionDto>;

const measurementClaimState = z.enum([
  "not_enough_controlled_runs",
  "mechanism_only",
  "inconclusive",
  "no_detected_difference",
  "accuracy_improved",
  "cost_efficiency_improved",
  "overall_improved",
  "regressed",
]);
const measurementStageStatus = z.enum(["complete", "failed", "pending", "missing", "not_applicable"]);
const fullSpineDto = z.object({
  recognized: measurementStageStatus,
  compiled: measurementStageStatus,
  admitted: measurementStageStatus,
  verified: measurementStageStatus,
  verdictDelivered: measurementStageStatus,
  acknowledged: measurementStageStatus,
  downstreamAction: measurementStageStatus,
  rerun: measurementStageStatus,
  finalOracle: measurementStageStatus,
});
const measurementWorkProductKind = z.enum([
  "coding_project",
  "ui_project",
  "api_contract",
  "migration",
  "security",
  "documentation",
  "custom",
]);
const measurementOracleKind = z.enum(["command", "browser", "reviewer", "static", "compound"]);

export const agentImprovementMeasurementPilotDto = z.object({
  id: namespacedId,
  workspaceId: namespacedId,
  environment: envName,
  title: z.string().min(1),
  status: z.enum(["not_started", "diagnostic_only", "mechanism_partial", "mechanism_smoke_complete", "smoke_complete", "directional", "claimable"]),
  claimState: measurementClaimState,
  claimLabel: z.string().min(1),
  claimDetail: z.string().min(1),
  preregisteredEnvelope: z.object({
    hash: z.string().min(1),
    frozenOracleHash: z.string().min(1),
    primaryEndpoint: z.literal("verified_success_rate_itt"),
    analysisRule: z.string().min(1),
    taskClassWeights: z.array(z.object({
      taskClass: z.string().min(1),
      weight: z.number().positive(),
    })).min(1),
  }),
  sample: z.object({
    pairCount: z.number().int().nonnegative(),
    taskClassCount: z.number().int().nonnegative(),
    completedOraclePairCount: z.number().int().nonnegative(),
    powered: z.boolean(),
    reviewerApprovedAt: iso.nullable(),
    decisionRulePassed: z.boolean(),
    claimKind: z.enum(["accuracy", "cost_efficiency", "overall"]).nullable(),
  }),
  localPredicateCompiler: z.object({
    provider: z.literal("ollama"),
    model: z.literal("qwen2.5-coder:7b"),
    state: z.enum(["configured", "used", "fail_closed", "missing"]),
    usedRunCount: z.number().int().nonnegative(),
    failClosedRunCount: z.number().int().nonnegative(),
    detail: z.string().min(1),
  }),
  workScenarios: z.array(z.object({
    id: namespacedId,
    title: z.string().min(1),
    workProductKind: measurementWorkProductKind,
    sourceProject: z.string().min(1),
    inputContract: z.string().min(1),
    oracleContract: z.string().min(1),
    artifact: z.string().min(1).nullable(),
  })).min(1),
  inheritance: z.object({
    status: z.enum(["not_started", "queued", "delivered", "acknowledged", "applied", "verified", "failed"]),
    sourceLearningId: namespacedId.nullable(),
    receivingAgentId: namespacedId.nullable(),
    receivingSessionId: namespacedId.nullable(),
    evidence: z.array(z.string().min(1)),
    detail: z.string().min(1),
  }),
  evidenceArtifacts: z.array(z.object({
    label: z.string().min(1),
    ref: z.string().min(1),
    path: z.string().min(1),
    hash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    embeddedJson: z.string().min(1),
    embeddedBase64: embeddedBase64.optional(),
    mediaType: z.string().min(1).optional(),
    verifiedAt: iso,
  }).superRefine((artifact, ctx) => {
    if (artifact.embeddedBase64 && !artifact.mediaType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["mediaType"],
        message: "binary evidence artifacts require an explicit media type",
      });
    }
  })).optional(),
  workItems: z.array(z.object({
    id: namespacedId,
    scenarioId: namespacedId,
    pairId: namespacedId,
    taskClass: z.string().min(1),
    repo: z.string().min(1),
    workProductKind: measurementWorkProductKind,
    arm: z.enum(["baseline", "excellent_assisted"]),
    assigned: z.boolean(),
    agentId: namespacedId,
    oracle: z.object({
      kind: measurementOracleKind,
      command: z.string().min(1).nullable(),
      artifact: z.string().min(1).nullable(),
    }),
    evidenceBundle: z.object({
      before: z.array(z.string().min(1)),
      after: z.array(z.string().min(1)),
      communication: z.array(z.string().min(1)),
      costs: z.array(z.string().min(1)),
    }),
    phase: z.enum(["observing", "judging", "repairing", "verified", "inconclusive"]),
    verdict: z.enum(["PASS", "MISMATCH", "FAIL", "UNKNOWN", "NOT_RUN", "PENDING"]),
    claimState: measurementClaimState,
    fullSpine: fullSpineDto,
    stageEvidence: z.record(z.string(), z.string().min(1)),
    attrition: z.string().min(1).nullable(),
    nextAction: z.string().min(1),
    costComplete: z.boolean(),
  })).min(1),
  predicatePipeline: z.array(z.object({
    stage: z.string().min(1),
    status: measurementStageStatus,
    detail: z.string().min(1),
    artifact: z.string().min(1).nullable(),
  })).min(1),
  outcomeClaims: z.array(z.object({
    metric: z.enum(["accuracy", "false_pass", "cost_per_verified_done", "time_to_verified_done", "repair_completion"]),
    claimState: measurementClaimState,
    value: z.number().nullable(),
    interval: z.object({
      low: z.number(),
      high: z.number(),
      unit: z.string().min(1),
    }).nullable(),
    successNonInferior: z.boolean().nullable(),
    detail: z.string().min(1),
  })).min(1),
}).superRefine((pilot, ctx) => {
  const improvedStates = new Set([
    "accuracy_improved",
    "cost_efficiency_improved",
    "overall_improved",
  ]);
  const causalOutcomeStates = new Set([
    ...improvedStates,
    "regressed",
    "no_detected_difference",
  ]);
  const requiresClaimableProof = pilot.status === "claimable" || causalOutcomeStates.has(pilot.claimState);
  if (requiresClaimableProof) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["status"],
      message: "claimable measurement pilots require server-derived durable Work/Attempt/Proof projections; self-declared dashboard payloads cannot promote claims",
    });
    if (pilot.localPredicateCompiler.state !== "used") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["localPredicateCompiler", "state"],
        message: "claimable pilot evidence requires recorded local predicate compiler usage",
      });
    }
    const assignedAssistedCount = pilot.workItems.filter((item) => item.assigned && item.arm === "excellent_assisted").length;
    if (pilot.localPredicateCompiler.usedRunCount < assignedAssistedCount || pilot.localPredicateCompiler.failClosedRunCount > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["localPredicateCompiler", "usedRunCount"],
        message: "claimable pilot evidence requires successful local compiler usage bound to every assigned assisted run",
      });
    }
    if (pilot.sample.pairCount < 24 || pilot.sample.completedOraclePairCount !== pilot.sample.pairCount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sample"],
        message: "claimable pilot evidence requires at least 24 assigned pairs and completed final oracles for every pair",
      });
    }
    if (!pilot.sample.powered || !pilot.sample.reviewerApprovedAt || !pilot.sample.decisionRulePassed || !pilot.sample.claimKind) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sample"],
        message: "claimable pilot evidence requires powered, reviewed evidence and a passing preregistered decision rule",
      });
    }
    if (pilot.inheritance.status !== "verified") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["inheritance", "status"],
        message: "claimable pilot evidence requires verified next-agent learning inheritance",
      });
    }
    const durableRef = /^(?:proof:sha256:[a-f0-9]{64}|work:[A-Za-z0-9._-]+:sha256:[a-f0-9]{64}|attempt:[A-Za-z0-9._-]+:sha256:[a-f0-9]{64})(#[A-Za-z0-9_./:-]+)?$/;
    for (const [index, ref] of pilot.inheritance.evidence.entries()) {
      if (!durableRef.test(ref)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["inheritance", "evidence", index],
          message: "claimable inheritance evidence must reference durable Work/Attempt/Proof records",
        });
      }
    }
    for (const [index, item] of pilot.workItems.entries()) {
      if (!item.assigned) continue;
      const refs = [
        item.oracle.artifact,
        ...item.evidenceBundle.before,
        ...item.evidenceBundle.after,
        ...item.evidenceBundle.communication,
        ...item.evidenceBundle.costs,
        ...Object.values(item.stageEvidence),
      ].filter((ref): ref is string => Boolean(ref));
      for (const ref of refs) {
        if (!durableRef.test(ref)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["workItems", index, "stageEvidence"],
            message: "claimable work evidence must reference durable Work/Attempt/Proof records",
          });
        }
      }
    }
  }
  if (improvedStates.has(pilot.claimState)) {
    if (pilot.status !== "claimable" || !pilot.sample.powered || pilot.sample.pairCount < 24) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["claimState"],
        message: "improved pilot claims require a powered claimable sample with at least 24 pairs",
      });
    }
    if (!pilot.sample.reviewerApprovedAt || !pilot.sample.decisionRulePassed || !pilot.sample.claimKind) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sample"],
        message: "improved pilot claims require reviewed evidence integrity and a passing preregistered decision rule",
      });
    }
  }
  if (pilot.claimState === "cost_efficiency_improved") {
    const costClaim = pilot.outcomeClaims.find((claim) => claim.metric === "cost_per_verified_done");
    if (!costClaim || costClaim.claimState !== "cost_efficiency_improved" || costClaim.successNonInferior !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["outcomeClaims"],
        message: "cost efficiency improvement requires a cost claim plus verified-success non-inferiority",
      });
    }
  }
  if (pilot.status === "smoke_complete") {
    if (pilot.localPredicateCompiler.state !== "used") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["localPredicateCompiler", "state"],
        message: "smoke_complete requires recorded local predicate compiler usage",
      });
    }
    if (pilot.sample.pairCount < 1 || pilot.sample.completedOraclePairCount < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sample"],
        message: "smoke_complete requires at least one assigned pair with completed final oracles",
      });
    }
  }
  if (pilot.status === "mechanism_smoke_complete") {
    if (pilot.localPredicateCompiler.state !== "used") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["localPredicateCompiler", "state"],
        message: "mechanism_smoke_complete requires recorded local predicate compiler execution",
      });
    }
    if (pilot.localPredicateCompiler.usedRunCount !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["localPredicateCompiler", "usedRunCount"],
        message: "mechanism_smoke_complete must not count assisted-run compiler usage",
      });
    }
    if (pilot.sample.pairCount !== 0 || pilot.sample.completedOraclePairCount !== 0 || pilot.sample.taskClassCount !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["sample"],
        message: "mechanism_smoke_complete must not count experimental pairs",
      });
    }
    if (pilot.workItems.some((item) => item.assigned)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["workItems"],
        message: "mechanism_smoke_complete must not contain assigned experimental work items",
      });
    }
    const integratedRepairStage = pilot.predicatePipeline.find((stage) => stage.stage === "Isolated local-agent repair");
    if (!integratedRepairStage || integratedRepairStage.status !== "complete") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["predicatePipeline"],
        message: "mechanism_smoke_complete requires an integrated isolated local-agent repair stage",
      });
    }
    const assistedItems = pilot.workItems.filter((item) => item.arm === "excellent_assisted");
    if (
      assistedItems.length === 0 ||
      assistedItems.some((item) =>
        !item.evidenceBundle.communication.length ||
        item.fullSpine.verdictDelivered !== "complete" ||
        item.fullSpine.acknowledged !== "complete" ||
        item.fullSpine.downstreamAction !== "complete" ||
        item.fullSpine.rerun !== "complete" ||
        item.fullSpine.finalOracle !== "complete"
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["workItems"],
        message: "mechanism_smoke_complete requires assisted communication, repair, rerun, and final-oracle evidence",
      });
    }
    if (pilot.inheritance.status !== "verified") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["inheritance", "status"],
        message: "mechanism_smoke_complete requires verified harness inheritance mechanics",
      });
    }
  }
  if (!requiresClaimableProof) {
    for (const [index, claim] of pilot.outcomeClaims.entries()) {
      if (causalOutcomeStates.has(claim.claimState)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["outcomeClaims", index, "claimState"],
          message: "nested causal outcome claims require server-derived durable Work/Attempt/Proof projections",
        });
      }
    }
    for (const [index, item] of pilot.workItems.entries()) {
      if (causalOutcomeStates.has(item.claimState)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["workItems", index, "claimState"],
          message: "work-item causal outcome claims require server-derived durable Work/Attempt/Proof projections",
        });
      }
    }
  }
  if ((pilot.inheritance.status === "applied" || pilot.inheritance.status === "verified") && pilot.inheritance.evidence.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["inheritance", "evidence"],
      message: "applied or verified learning inheritance requires evidence references",
    });
  }
  if (pilot.inheritance.status === "verified") {
    if (!pilot.inheritance.sourceLearningId || !pilot.inheritance.receivingAgentId || !pilot.inheritance.receivingSessionId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["inheritance"],
        message: "verified learning inheritance requires source learning, receiving agent, and receiving session ids",
      });
    }
  }
  const scenariosById = new Map(pilot.workScenarios.map((scenario) => [scenario.id, scenario]));
  for (const [index, item] of pilot.workItems.entries()) {
    const scenario = scenariosById.get(item.scenarioId);
    if (!scenario) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["workItems", index, "scenarioId"],
        message: "measurement work items must reference a registered work scenario",
      });
    } else if (scenario.workProductKind !== item.workProductKind) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["workItems", index, "workProductKind"],
        message: "measurement work item kind must match its registered work scenario",
      });
    }
    if (item.oracle.kind === "command" && !item.oracle.command) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["workItems", index, "oracle", "command"],
        message: "command oracle work items require the frozen command",
      });
    }
    if (item.assigned && item.evidenceBundle.before.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["workItems", index, "evidenceBundle", "before"],
        message: "assigned measurement work items require before evidence",
      });
    }
    if (item.assigned && item.evidenceBundle.after.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["workItems", index, "evidenceBundle", "after"],
        message: "assigned measurement work items require after evidence",
      });
    }
    if (
      item.arm === "excellent_assisted" &&
      (item.fullSpine.verdictDelivered === "complete" ||
        item.fullSpine.acknowledged === "complete" ||
        item.fullSpine.downstreamAction === "complete") &&
      item.evidenceBundle.communication.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["workItems", index, "evidenceBundle", "communication"],
        message: "assisted talk-back stages require same-session communication evidence",
      });
    }
    for (const [stage, status] of Object.entries(item.fullSpine)) {
      if (status === "complete" && !item.stageEvidence[stage]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["workItems", index, "stageEvidence", stage],
          message: "completed full-spine stages require evidence references",
        });
      }
    }
    if (item.arm !== "excellent_assisted") continue;
    for (const [stage, status] of Object.entries(item.fullSpine)) {
      if (status === "not_applicable") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["workItems", index, "fullSpine", stage],
          message: "assisted causal runs must record every full-spine stage",
        });
      }
      if ((pilot.status === "smoke_complete" || requiresClaimableProof) && item.assigned && status !== "complete") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["workItems", index, "fullSpine", stage],
          message: "completed or claimable assisted work items require every full-spine stage to be complete",
        });
      }
    }
  }
});
export type AgentImprovementMeasurementPilotDto = z.infer<typeof agentImprovementMeasurementPilotDto>;

const activeLoopEvidenceStageState = z.enum(["missing", "observed", "verified", "suppressed"]);
const activeLoopPhase = z.enum([
  "observing",
  "classifying",
  "intervening",
  "verifying",
  "learning",
  "blocked_by_policy",
]);
export type ActiveLoopPhase = z.infer<typeof activeLoopPhase>;
const activeLoopEvidenceArtifactDto = z.object({
  ref: z.string().min(1),
  kind: z.string().min(1),
  state: activeLoopEvidenceStageState,
  contentHash: z.string().regex(/^[a-f0-9]{64}$/),
  sourceRef: z.string().min(1).nullable(),
  sensitivity: z.string().min(1),
  payloadKind: z.string().min(1),
  classificationVersion: z.string().min(1),
  classificationDecisionId: z.string().min(1).nullable(),
  issuerId: z.string().min(1).nullable(),
  issuerKind: z.string().min(1).nullable(),
  sessionUrn: namespacedId,
  agentId: namespacedId,
  createdAt: iso,
});
export const activeLoopEvidenceDto = z.object({
  workspaceId: namespacedId,
  environment: envName,
  actorId: namespacedId.nullable(),
  state: activeLoopEvidenceStageState,
  activePhase: activeLoopPhase,
  stages: z.object({
    before: activeLoopEvidenceStageState,
    localModel: activeLoopEvidenceStageState,
    delivery: activeLoopEvidenceStageState,
    acknowledgement: activeLoopEvidenceStageState,
    after: activeLoopEvidenceStageState,
    runManifest: activeLoopEvidenceStageState,
    oracle: activeLoopEvidenceStageState,
    selector: activeLoopEvidenceStageState,
    transfer: activeLoopEvidenceStageState,
  }),
  artifacts: z.array(activeLoopEvidenceArtifactDto),
  localInvocationReceiptRefs: z.array(z.string().min(1)),
  transferExposureIds: z.array(namespacedId),
  detail: z.string().min(1),
});
export type ActiveLoopEvidenceDto = z.infer<typeof activeLoopEvidenceDto>;

const activeLoopCanaryClaimState = z.enum(["canary", "not_claimable"]);
export type ActiveLoopCanaryClaimState = z.infer<typeof activeLoopCanaryClaimState>;
const activeLoopCanaryManifestStatus = z.enum([
  "missing",
  "preregistered_not_materialized",
  "materialized_unsigned",
  "materialized_signed",
]);
export type ActiveLoopCanaryManifestStatus = z.infer<typeof activeLoopCanaryManifestStatus>;
const activeLoopCanaryArmStatus = z.enum(["missing", "present", "verified"]);
export type ActiveLoopCanaryArmStatus = z.infer<typeof activeLoopCanaryArmStatus>;
const activeLoopCanaryArmDto = z.object({
  arm: z.enum([
    "source_baseline",
    "source_placebo",
    "source_full_loop",
    "transfer_baseline_fresh_agent",
    "transfer_full_loop_fresh_agent",
    "counterevidence_challenge_fresh_agent",
  ]),
  status: activeLoopCanaryArmStatus,
  missingReasons: z.array(z.string().min(1)),
});
export type ActiveLoopCanaryArmDto = z.infer<typeof activeLoopCanaryArmDto>;
export const activeLoopCanaryDto = z.object({
  manifestId: z.string().min(1),
  manifestStatus: activeLoopCanaryManifestStatus,
  claimState: activeLoopCanaryClaimState,
  canMaterialize: z.boolean(),
  materialized: z.boolean(),
  signed: z.boolean(),
  manifestHash: z.string().min(1).nullable(),
  assignedRunCount: z.number().int().nonnegative(),
  missingDataCount: z.number().int().nonnegative(),
  missingReasons: z.array(z.string().min(1)),
  arms: z.array(activeLoopCanaryArmDto),
  evidenceSource: z.string().min(1),
  detail: z.string().min(1),
}).superRefine((canary, ctx) => {
  if (canary.claimState === "canary" && (!canary.materialized || !canary.signed || canary.missingReasons.length > 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["claimState"],
      message: "canary claim requires materialized signed manifest and no missing evidence",
    });
  }
  if (canary.canMaterialize && canary.claimState !== "canary") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["canMaterialize"],
      message: "canary materialization readiness cannot exceed claim state",
    });
  }
});
export type ActiveLoopCanaryDto = z.infer<typeof activeLoopCanaryDto>;

export const agentImprovementDashboardResponse = z.object({
  schemaVersion: z.literal(AGENT_IMPROVEMENT_DASHBOARD_SCHEMA_VERSION),
  provenance: agentImprovementProvenanceDto,
  claimSummary: agentImprovementClaimSummaryDto,
  evidenceDetails: agentImprovementEvidenceDetailsDto,
  measurementPilot: agentImprovementMeasurementPilotDto,
  activeLoopEvidence: activeLoopEvidenceDto,
  activeLoopCanary: activeLoopCanaryDto,
  agentConnections: z.array(agentConnectionDto),
  learningExposures: z.array(learningExposureAttemptDto),
  learningSummaries: z.array(agentLearningSummaryDto),
  evidenceManifests: z.array(improvementEvidenceManifestDto),
  improvementMetrics: z.array(improvementMetricDto),
  modelRoutingComparisons: z.array(modelRoutingComparisonDto),
  localVerificationModels: z.array(localVerificationModelDto),
  guardrailDecisions: z.array(guardrailDecisionDto),
  suppressedFacts: z.array(suppressedFactDto),
}).superRefine((response, ctx) => {
  const manifestsById = new Map(response.evidenceManifests.map((manifest) => [manifest.id, manifest]));
  const exposuresById = new Map(response.learningExposures.map((exposure) => [exposure.id, exposure]));
  const latestAttemptByKey = new Map<string, number>();
  const attemptsByLogicalKey = new Map<string, typeof response.learningExposures>();
  const attemptKeys = new Set<string>();
  const recommendedRoutes = new Set<string>();
  const improvedPilotStates = new Set([
    "accuracy_improved",
    "cost_efficiency_improved",
    "overall_improved",
  ]);
  const causalOutcomeStates = new Set([
    ...improvedPilotStates,
    "regressed",
    "no_detected_difference",
  ]);
  const measurementPilot = response.measurementPilot;
  if (measurementPilot.workspaceId !== response.provenance.workspaceId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["measurementPilot", "workspaceId"],
      message: "measurement pilot workspace must match response provenance",
    });
  }
  if (measurementPilot.environment !== response.provenance.environment) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["measurementPilot", "environment"],
      message: "measurement pilot environment must match response provenance",
    });
  }
  const assignedPairArms = new Map<string, Set<"baseline" | "excellent_assisted">>();
  const completedOraclePairArms = new Map<string, Set<"baseline" | "excellent_assisted">>();
  const pairClasses = new Map<string, string>();
  for (const item of measurementPilot.workItems) {
    if (item.assigned) {
      const arms = assignedPairArms.get(item.pairId) ?? new Set<"baseline" | "excellent_assisted">();
      arms.add(item.arm);
      assignedPairArms.set(item.pairId, arms);
    }
    if (
      item.assigned &&
      (item.verdict === "PASS" || item.verdict === "FAIL") &&
      (item.phase === "verified" || item.phase === "inconclusive") &&
      item.fullSpine.finalOracle === "complete"
    ) {
      const arms = completedOraclePairArms.get(item.pairId) ?? new Set<"baseline" | "excellent_assisted">();
      arms.add(item.arm);
      completedOraclePairArms.set(item.pairId, arms);
    }
    pairClasses.set(item.pairId, item.taskClass);
  }
  const assignedPairs = [...assignedPairArms.entries()].filter(([, arms]) => arms.has("baseline") && arms.has("excellent_assisted"));
  const completedOraclePairs = [...completedOraclePairArms.entries()].filter(([, arms]) => arms.has("baseline") && arms.has("excellent_assisted"));
  if (measurementPilot.sample.pairCount !== assignedPairs.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["measurementPilot", "sample", "pairCount"],
      message: "measurement pilot pairCount must equal assigned baseline/assisted pairs in workItems",
    });
  }
  if (measurementPilot.sample.completedOraclePairCount !== completedOraclePairs.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["measurementPilot", "sample", "completedOraclePairCount"],
      message: "measurement pilot completedOraclePairCount must equal pairs with completed final oracles on both arms",
    });
  }
  const validTaskClasses = new Set(assignedPairs.map(([pairId]) => pairClasses.get(pairId)).filter(Boolean));
  if (measurementPilot.sample.taskClassCount !== validTaskClasses.size) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["measurementPilot", "sample", "taskClassCount"],
      message: "measurement pilot taskClassCount must equal task classes represented by valid pairs",
    });
  }
  for (const [pairId, arms] of assignedPairArms) {
    if (measurementPilot.sample.pairCount > 0 && (!arms.has("baseline") || !arms.has("excellent_assisted"))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["measurementPilot", "workItems"],
        message: `measurement pilot pair ${pairId} must include exactly one baseline and one assisted arm`,
      });
    }
    const rows = measurementPilot.workItems.filter((item) => item.pairId === pairId);
    if (measurementPilot.sample.pairCount > 0 && rows.length !== 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["measurementPilot", "workItems"],
        message: `measurement pilot pair ${pairId} must include exactly two work items`,
      });
    }
  }
  if (!causalOutcomeStates.has(measurementPilot.claimState)) {
    if (/\b(can claim|improved by|\+\d+\s*pts|% improvement)\b/i.test(`${measurementPilot.claimLabel} ${measurementPilot.claimDetail}`)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["measurementPilot", "claimLabel"],
        message: "non-improved measurement pilot labels/details must not overclaim outcome improvement",
      });
    }
    for (const [index, claim] of measurementPilot.outcomeClaims.entries()) {
      if (causalOutcomeStates.has(claim.claimState)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["measurementPilot", "outcomeClaims", index, "claimState"],
          message: "outcome claim state cannot exceed a non-causal pilot claim state",
        });
      }
    }
    for (const [index, item] of measurementPilot.workItems.entries()) {
      if (causalOutcomeStates.has(item.claimState)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["measurementPilot", "workItems", index, "claimState"],
          message: "work-item claim state cannot exceed a non-causal pilot claim state",
        });
      }
    }
  }
  for (const [index, row] of response.localVerificationModels.entries()) {
    if (row.workspaceId !== response.provenance.workspaceId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["localVerificationModels", index, "workspaceId"],
        message: "local verification model workspace must match response provenance",
      });
    }
    if (row.environment !== response.provenance.environment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["localVerificationModels", index, "environment"],
        message: "local verification model environment must match response provenance",
      });
    }
  }
  for (const exposure of response.learningExposures) {
    const scopedAttemptKey = [
      exposure.workspaceId,
      exposure.environment,
      exposure.logicalExposureKey,
      exposure.attemptNumber,
    ].join("::");
    if (attemptKeys.has(scopedAttemptKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["learningExposures"],
        message: "response must not include duplicate scoped logical exposure attempts",
      });
    }
    attemptKeys.add(scopedAttemptKey);
    const scopedLogicalKey = [exposure.workspaceId, exposure.environment, exposure.logicalExposureKey].join("::");
    const attempts = attemptsByLogicalKey.get(scopedLogicalKey) ?? [];
    attempts.push(exposure);
    attemptsByLogicalKey.set(scopedLogicalKey, attempts);
    latestAttemptByKey.set(
      scopedLogicalKey,
      Math.max(latestAttemptByKey.get(scopedLogicalKey) ?? 0, exposure.attemptNumber),
    );
  }
  for (let index = 0; index < response.agentConnections.length; index++) {
    const connection = response.agentConnections[index];
    if (connection?.workspaceId !== response.provenance.workspaceId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["agentConnections", index, "workspaceId"],
        message: "connection workspace must match response provenance",
      });
    }
    if (connection?.environment !== response.provenance.environment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["agentConnections", index, "environment"],
        message: "connection environment must match response provenance",
      });
    }
    if (connection?.asOf !== response.provenance.asOf) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["agentConnections", index, "asOf"],
        message: "connection asOf must match response provenance asOf",
      });
    }
    if (
      connection?.state === "connected" &&
      connection.latestHeartbeatAt &&
      Date.parse(connection.latestHeartbeatAt) > Date.parse(response.provenance.asOf)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["agentConnections", index, "latestHeartbeatAt"],
        message: "connected heartbeat cannot be in the future relative to response provenance",
      });
    }
  }
  for (const [scopedLogicalKey, attempts] of attemptsByLogicalKey) {
    attempts.sort((a, b) => a.attemptNumber - b.attemptNumber);
    for (let index = 0; index < attempts.length; index++) {
      const attempt = attempts[index];
      if (!attempt) continue;
      if (attempt.retryCount !== attempt.attemptNumber - 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["learningExposures"],
          message: "retryCount must equal attemptNumber - 1",
        });
      }
      if (attempt.attemptNumber > 1 && !attempt.lastRetryAt) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["learningExposures"],
          message: "retry attempts require lastRetryAt",
        });
      }
      if (attempt.attemptNumber !== index + 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["learningExposures"],
          message: `attempts for ${scopedLogicalKey} must be contiguous from 1`,
        });
      }
      const previous = attempts[index - 1];
      if (attempt.attemptNumber > 1 && previous?.status !== "failed" && previous?.status !== "stale") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["learningExposures"],
          message: "retry attempts require a failed or stale predecessor",
        });
      }
    }
  }
  for (let index = 0; index < response.learningExposures.length; index++) {
    const exposure = response.learningExposures[index];
    if (exposure?.workspaceId !== response.provenance.workspaceId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["learningExposures", index, "workspaceId"],
        message: "learning exposure workspace must match response provenance",
      });
    }
    if (exposure?.environment !== response.provenance.environment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["learningExposures", index, "environment"],
        message: "learning exposure environment must match response provenance",
      });
    }
    const scopedLogicalKey = [exposure?.workspaceId, exposure?.environment, exposure?.logicalExposureKey].join("::");
    const latestAttempt = latestAttemptByKey.get(scopedLogicalKey);
    if (
      exposure &&
      latestAttempt !== undefined &&
      exposure.attemptNumber < latestAttempt &&
      canRenderLearningDelivered(exposure)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["learningExposures", index, "attemptNumber"],
        message: "only the latest non-superseded attempt can render delivered",
      });
    }
  }
  for (let index = 0; index < response.learningSummaries.length; index++) {
    const learning = response.learningSummaries[index];
    if (learning?.workspaceId !== response.provenance.workspaceId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["learningSummaries", index, "workspaceId"],
        message: "learning summary workspace must match response provenance",
      });
    }
    if (learning?.environment !== response.provenance.environment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["learningSummaries", index, "environment"],
        message: "learning summary environment must match response provenance",
      });
    }
    const exposure = learning?.exposureId ? exposuresById.get(learning.exposureId) : null;
    if (learning?.exposureId && !exposure) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["learningSummaries", index, "exposureId"],
        message: "learning summary exposureId must reference an exposure in this response",
      });
    }
    if ((learning?.status === "delivered" || learning?.status === "applied") && !exposure) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["learningSummaries", index, "status"],
        message: "delivered/applied learning summaries require linked exposure evidence",
      });
    }
    if (
      (learning?.status === "approved" || learning?.status === "delivered" || learning?.status === "applied") &&
      !learning.approvedAt
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["learningSummaries", index, "approvedAt"],
        message: "approved/delivered/applied learning summaries require approval timestamp",
      });
    }
    if (learning?.status === "delivered" && exposure && !canRenderLearningDelivered(exposure)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["learningSummaries", index, "status"],
        message: "delivered learning summaries require delivered exposure evidence",
      });
    }
    if (learning?.status === "applied" && exposure && exposure.status !== "applied") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["learningSummaries", index, "status"],
        message: "applied learning summaries require applied exposure evidence",
      });
    }
  }
  for (let index = 0; index < response.evidenceManifests.length; index++) {
    const manifest = response.evidenceManifests[index];
    if (manifest?.workspaceId !== response.provenance.workspaceId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["evidenceManifests", index, "workspaceId"],
        message: "manifest workspace must match response provenance",
      });
    }
    if (manifest?.environment !== response.provenance.environment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["evidenceManifests", index, "environment"],
        message: "manifest environment must match response provenance",
      });
    }
    for (const exposureId of manifest?.exposureIds ?? []) {
      const exposure = exposuresById.get(exposureId);
      if (!exposure) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["evidenceManifests", index, "exposureIds"],
          message: "manifest exposureIds must reference exposures in this response",
        });
      }
      if (manifest?.attributionClass === "controlled-improvement" && exposure && !canRenderLearningDelivered(exposure)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["evidenceManifests", index, "exposureIds"],
          message: "controlled-improvement manifests require delivered exposure evidence",
        });
      }
    }
  }
  for (let index = 0; index < response.improvementMetrics.length; index++) {
    const metric = response.improvementMetrics[index];
    if (metric?.workspaceId !== response.provenance.workspaceId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["improvementMetrics", index, "workspaceId"],
        message: "metric workspace must match response provenance",
      });
    }
    if (metric?.environment !== response.provenance.environment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["improvementMetrics", index, "environment"],
        message: "metric environment must match response provenance",
      });
    }
    const manifest = metric?.evidenceManifestId ? manifestsById.get(metric.evidenceManifestId) : null;
    if (metric?.evidenceManifestId && !manifest) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["improvementMetrics", index, "evidenceManifestId"],
        message: "metric evidenceManifestId must reference a manifest in this response",
      });
    }
    if (metric?.attributionClass === "controlled-improvement" && !metric.evidenceManifestId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["improvementMetrics", index, "evidenceManifestId"],
        message: "controlled-improvement metrics require an evidence manifest",
      });
    }
    if (metric?.evidenceManifestId && manifest && metric.attributionClass !== manifest.attributionClass) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["improvementMetrics", index, "attributionClass"],
        message: "metric attributionClass must match its evidence manifest",
      });
    }
    if (
      metric?.attributionClass === "controlled-improvement" &&
      (metric.value === null || metric.baselineValue === null || metric.currentValue === null)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["improvementMetrics", index, "value"],
        message: "controlled-improvement metrics require measured value, baselineValue, and currentValue",
      });
    }
  }
  for (let index = 0; index < response.modelRoutingComparisons.length; index++) {
    const row = response.modelRoutingComparisons[index];
    if (!row?.successDeltaLabel) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["modelRoutingComparisons", index, "successDeltaLabel"],
        message: "dashboard routing rows require server-derived success delta labels",
      });
    }
    if (!row?.successDeltaState) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["modelRoutingComparisons", index, "successDeltaState"],
        message: "dashboard routing rows require server-derived success delta state",
      });
    }
    if (row?.workspaceId !== response.provenance.workspaceId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["modelRoutingComparisons", index, "workspaceId"],
        message: "routing comparison workspace must match response provenance",
      });
    }
    if (row?.environment !== response.provenance.environment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["modelRoutingComparisons", index, "environment"],
        message: "routing comparison environment must match response provenance",
      });
    }
    const manifest = row?.evidenceManifestId ? manifestsById.get(row.evidenceManifestId) : null;
    if (row?.evidenceManifestId && !manifest) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["modelRoutingComparisons", index, "evidenceManifestId"],
        message: "routing comparison evidenceManifestId must reference a manifest in this response",
      });
    }
    if (row?.recommended && row.attributionClass === "insufficient-data") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["modelRoutingComparisons", index, "recommended"],
        message: "recommended routing rows require complete controlled comparison evidence",
      });
    }
    if (row?.recommended) {
      const scopedRouteKey = [row.workspaceId, row.environment, row.routeKey].join("::");
      if (recommendedRoutes.has(scopedRouteKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["modelRoutingComparisons", index, "recommended"],
          message: "only one routing comparison can be recommended per scoped route",
        });
      }
      recommendedRoutes.add(scopedRouteKey);
      if (
        row.attributionClass !== "controlled-improvement" ||
        row.sampleSize <= 0 ||
        row.successRate === null ||
        row.averageCostUsd === null ||
        row.averageDurationMs === null ||
        !row.evidenceManifestId ||
        !manifest ||
        !canRenderControlledImprovement(manifest)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["modelRoutingComparisons", index, "recommended"],
          message: "recommended routing rows require controlled evidence, non-null metrics, positive sample, and reviewed manifest",
        });
      }
    }
  }
});
export type AgentImprovementDashboardResponse = z.infer<typeof agentImprovementDashboardResponse>;

export const agentImprovementManifestIngestBody = z.object({
  actorId: namespacedId,
  manifest: improvementEvidenceManifestDto,
  metrics: z.array(improvementMetricDto),
  routingComparisons: z.array(modelRoutingComparisonDto),
}).strict();
export type AgentImprovementManifestIngestBody = z.infer<typeof agentImprovementManifestIngestBody>;

export const agentImprovementMeasurementReceiptIngestBody = z.object({
  actorId: namespacedId,
  receiptId: namespacedId,
  receipt: agentImprovementMeasurementPilotDto,
}).strict();
export type AgentImprovementMeasurementReceiptIngestBody = z.infer<typeof agentImprovementMeasurementReceiptIngestBody>;

export const agentImprovementManifestIngestResponse = z.object({
  schemaVersion: z.literal(AGENT_IMPROVEMENT_DASHBOARD_SCHEMA_VERSION),
  capability: z.literal("api:write"),
  dashboard: agentImprovementDashboardResponse,
});
export type AgentImprovementManifestIngestResponse = z.infer<typeof agentImprovementManifestIngestResponse>;

export const agentImprovementMeasurementReceiptIngestResponse = z.object({
  schemaVersion: z.literal(AGENT_IMPROVEMENT_DASHBOARD_SCHEMA_VERSION),
  capability: z.literal("api:write"),
  dashboard: agentImprovementDashboardResponse,
});
export type AgentImprovementMeasurementReceiptIngestResponse = z.infer<typeof agentImprovementMeasurementReceiptIngestResponse>;

export function resolveAgentConnectionState(
  evidence: Pick<
    AgentConnectionEvidenceDto,
    | "hasMalformedOrCrossWorkspaceOrPermissionBlockedRecords"
    | "hasEvidenceSourceUnavailable"
    | "hasActiveLiveReportInsideTtl"
    | "hasValidIntegrationConfigInsideTtl"
    | "hasWorkspaceScopedHistoricalObservation"
  >,
): AgentConnectionState {
  if (evidence.hasMalformedOrCrossWorkspaceOrPermissionBlockedRecords || evidence.hasEvidenceSourceUnavailable) {
    return "unknown";
  }
  if (evidence.hasActiveLiveReportInsideTtl) return "connected";
  if (evidence.hasValidIntegrationConfigInsideTtl) return "configured";
  if (evidence.hasWorkspaceScopedHistoricalObservation) return "observed";
  return "needs_setup";
}

export function canRenderLearningDelivered(attempt: LearningExposureAttemptDto): boolean {
  return (
    (attempt.status === "acknowledged" || attempt.status === "applied") &&
    Boolean(attempt.emittedAt) &&
    Boolean(attempt.acknowledgedAt) &&
    hasBoundAcknowledgement(attempt)
  );
}

export function canRenderControlledImprovement(manifest: ImprovementEvidenceManifestDto): boolean {
  if (manifest.environment !== "demo") return false;
  const numerator = new Set(manifest.numeratorRecordIds);
  const denominator = new Set(manifest.denominatorRecordIds);
  const overlap = [...numerator].some((id) => denominator.has(id));
  return (
    manifest.attributionClass === "controlled-improvement" &&
    Boolean(manifest.comparisonUnit) &&
    Boolean(manifest.policyVersion) &&
    manifest.sampleSize >= MIN_DEMO_CONTROLLED_RECORDS &&
    manifest.sampleSize === denominator.size &&
    overlap === false &&
    manifest.numeratorRecordIds.length > 0 &&
    manifest.denominatorRecordIds.length > 0 &&
    manifest.exposureIds.length > 0 &&
    manifest.sourceLinks.length >= manifest.sampleSize &&
    manifest.completeness.expectedSourceCount > 0 &&
    manifest.completeness.knownSourceCount === manifest.completeness.expectedSourceCount &&
    !manifest.completeness.unknownCostCoverage &&
    manifest.uncertaintyInterval !== null &&
    manifest.uncertaintyInterval.low > 0 &&
    manifest.uncertaintyInterval.low <= manifest.uncertaintyInterval.high &&
    hasIncreasingWindow(manifest.window) &&
    manifest.reviewerApprovedAt !== null
  );
}

export function canRenderRealizedSavings(manifest: ImprovementEvidenceManifestDto): boolean {
  return canRenderControlledImprovement(manifest) && Boolean(manifest.counterfactualBasis);
}

export function nextLearningExposureStatus(
  current: LearningExposureStatus,
  next: LearningExposureStatus,
): LearningExposureStatus {
  if (!legalLearningExposureTransitions[current].includes(next as never)) {
    throw new Error(`Illegal learning exposure transition: ${current} -> ${next}`);
  }
  return next;
}

export function ageLearningExposureAttempt(
  attempt: LearningExposureAttemptDto,
  nowMs: number,
  ttlsMs: {
    selectedToEmitted: number;
    acknowledgement: number;
    acknowledgedToApplied: number;
  },
): LearningExposureStatus {
  if (attempt.status === "selected") {
    return elapsedFromIso(attempt.selectedAt, nowMs) >= ttlsMs.selectedToEmitted ? "stale" : "selected";
  }
  if (attempt.status === "emitted") {
    if (!attempt.emittedAt) return "failed";
    return elapsedFromIso(attempt.emittedAt, nowMs) >= ttlsMs.acknowledgement ? "failed" : "emitted";
  }
  if (attempt.status === "acknowledged") {
    if (!attempt.acknowledgedAt) return "failed";
    return elapsedFromIso(attempt.acknowledgedAt, nowMs) >= ttlsMs.acknowledgedToApplied
      ? "stale"
      : "acknowledged";
  }
  return attempt.status;
}

function hasBoundAcknowledgement(attempt: LearningExposureAttemptDto): boolean {
  const evidence = attempt.acknowledgementEvidence;
  if (!evidence) return false;
  const hasReceiverSignal =
    Boolean(evidence.receiverAckId) ||
    Boolean(evidence.receiverEchoedContextHash) ||
    Boolean(evidence.receiverConfirmedResponseId);
  return (
    hasReceiverSignal &&
    evidence.receivingSessionId === attempt.receivingSessionId &&
    evidence.contextVersionHash === attempt.contextVersionHash
  );
}

function hasBoundApplication(attempt: LearningExposureAttemptDto): boolean {
  const evidence = attempt.appliedEvidence;
  if (!evidence) return false;
  const hasDownstreamRecord = Boolean(evidence.downstreamRunId) || Boolean(evidence.downstreamClaimId);
  return (
    hasDownstreamRecord &&
    evidence.receivingSessionId === attempt.receivingSessionId &&
    evidence.contextVersionHash === attempt.contextVersionHash &&
    (!evidence.exposureId || evidence.exposureId === attempt.id)
  );
}

export function logicalExposureKeyFor(input: Pick<
  LearningExposureAttemptDto,
  "workspaceId" | "environment" | "learningId" | "receivingSessionId" | "contextVersionHash" | "sourceSurface"
>): string {
  return JSON.stringify([
    input.workspaceId,
    input.environment,
    input.learningId,
    input.receivingSessionId,
    input.contextVersionHash,
    input.sourceSurface,
  ]);
}

function elapsedFromIso(isoTimestamp: string, nowMs: number): number {
  const parsed = Date.parse(isoTimestamp);
  if (Number.isNaN(parsed)) return Number.POSITIVE_INFINITY;
  return nowMs - parsed;
}

function hasIncreasingWindow(window: { startsAt: string; endsAt: string }): boolean {
  const startsAt = Date.parse(window.startsAt);
  const endsAt = Date.parse(window.endsAt);
  return !Number.isNaN(startsAt) && !Number.isNaN(endsAt) && startsAt < endsAt;
}

const dashboardGetOp: Operation<void, AgentImprovementQuery, void, AgentImprovementDashboardResponse> = {
  method: "GET",
  path: () => "/api/verification/agent-improvement",
  query: agentImprovementQuery,
  response: agentImprovementDashboardResponse,
};

const manifestIngestOp: Operation<
  void,
  AgentImprovementQuery,
  AgentImprovementManifestIngestBody,
  AgentImprovementManifestIngestResponse
> = {
  method: "POST",
  path: () => "/api/verification/agent-improvement/manifests",
  query: agentImprovementQuery,
  body: agentImprovementManifestIngestBody,
  response: agentImprovementManifestIngestResponse,
  invalidates: [dashboardGetOp],
};

const measurementReceiptIngestOp: Operation<
  void,
  AgentImprovementQuery,
  AgentImprovementMeasurementReceiptIngestBody,
  AgentImprovementMeasurementReceiptIngestResponse
> = {
  method: "POST",
  path: () => "/api/verification/agent-improvement/measurement-receipts",
  query: agentImprovementQuery,
  body: agentImprovementMeasurementReceiptIngestBody,
  response: agentImprovementMeasurementReceiptIngestResponse,
  invalidates: [dashboardGetOp],
};

export const agentImprovementDashboard = {
  dashboard: dashboardGetOp,
  ingestManifest: manifestIngestOp,
  ingestMeasurementReceipt: measurementReceiptIngestOp,
};
